// src/pages/PantallaRegistro.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import './PantallaRegistro.css';

const GENEROS = ['Masculino', 'Femenino', 'Prefiero no decirlo'];
const NIVELES_ESTUDIO = ['PreGrado', 'PostGrado', 'Otros'];

const PantallaRegistro = ({ volver }) => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nombre: '',
    apellido: '',
    edad: '',
    genero: '',
    mail: '',
    universidad: '',
    nivelEstudio: '',
    anioEstudio: '',
    usuario: '',
    contrasena: '',
  });

  // Estado para universidades (dinámico desde /config/universidades)
  const [universidades, setUniversidades] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const url = `${API_URL}/config/universidades`;
        console.log('[Registro] Cargando universidades desde:', url);
        const { data } = await axios.get(url, { timeout: 10000 });
        console.log('[Registro] Respuesta /config/universidades:', data);

        if (!alive) return;
        if (data?.ok && Array.isArray(data.universidades)) {
          setUniversidades(data.universidades);
        } else {
          console.error('[Registro] Backend devolvió ok=false o formato inesperado:', data);
          setUniversidades([]); // fallback vacío
        }
      } catch (e) {
        console.error('❌ No se pudieron cargar universidades dinámicas:', e);
        setUniversidades([]); // fallback vacío
      }
    })();
    return () => { alive = false; };
  }, []);

  const aniosDisponibles = useMemo(() => {
    if (form.nivelEstudio === 'PreGrado') return ['1', '2', '3', '4', '5', '6', '7'];
    if (form.nivelEstudio === 'PostGrado') return ['1', '2', '3'];
    if (form.nivelEstudio === 'Otros') return ['Otros'];
    return [];
  }, [form.nivelEstudio]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'nivelEstudio') {
      setForm((prev) => ({ ...prev, nivelEstudio: value, anioEstudio: '' }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validar = () => {
    // 1) Todos los campos obligatorios
    const faltantes = Object.entries(form).filter(([_, v]) => String(v ?? '').trim() === '');
    if (faltantes.length > 0) {
      alert('Debes completar todos los campos.');
      return false;
    }
    // 2) Rango de edad válido
    if (Number.isNaN(Number(form.edad)) || Number(form.edad) < 10 || Number(form.edad) > 100) {
      alert('Ingresa una Edad válida (entre 10 y 100).');
      return false;
    }
    // 3) Email válido
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.mail);
    if (!emailOk) {
      alert('Ingresa un correo electrónico válido.');
      return false;
    }
    // 4) Si el nivel cambia, año no puede estar vacío (ya lo cubre el “todos los campos”)
    return true;
  };

  const handleVolver = () => {
    if (typeof volver === 'function') {
      try { volver(); return; } catch {}
    }
    navigate('/'); // fallback
  };

  const registrarUsuario = async () => {
    if (!validar()) return;

    try {
      const payload = {
        nombre: form.nombre,
        apellido: form.apellido,
        edad: Number(form.edad),
        genero: form.genero,
        mail: form.mail,
        universidad: form.universidad,
        nivelEstudio: form.nivelEstudio,
        anioEstudio: form.anioEstudio,
        nombreUsuario: form.usuario,
        contrasena: form.contrasena,
      };

      const { data } = await axios.post(`${API_URL}/registrar`, payload);

      if (data?.ok) {
        alert('✅ Registro exitoso. Ahora puedes iniciar sesión.');
        handleVolver();
      } else if (data?.error === 'usuario_duplicado') {
        alert('El nombre de usuario ya existe. Por favor elige otro.');
      } else if (data?.error === 'mail_duplicado') {
        alert('El correo ya está registrado. Usa otro correo.');
      } else if (data?.error === 'datos_incompletos') {
        alert('Debes completar todos los campos.');
      } else {
        alert('❌ Error al registrar. Intenta más tarde.');
      }
    } catch (error) {
      console.error('❌ Error al registrar:', error);
      alert('Hubo un error al registrar. Inténtalo más tarde.');
    }
  };

  return (
    <div className="pantalla-registro">
      <div className="registro-card">
        <div className="registro-header">
          <h1>Registro Nuevo Usuario</h1>
          <h1>Turno24Rx</h1>
        </div>

        {/* Nombre y Apellido */}
        <div className="grid-2">
          <div className="field">
            <label htmlFor="nombre">Nombre</label>
            <input
              id="nombre"
              name="nombre"
              value={form.nombre}
              onChange={handleChange}
              autoComplete="given-name"
            />
          </div>
          <div className="field">
            <label htmlFor="apellido">Apellido</label>
            <input
              id="apellido"
              name="apellido"
              value={form.apellido}
              onChange={handleChange}
              autoComplete="family-name"
            />
          </div>
        </div>

        {/* Correo */}
        <div className="field">
          <label htmlFor="mail">Correo electrónico</label>
          <input
            id="mail"
            name="mail"
            type="email"
            value={form.mail}
            onChange={handleChange}
            autoComplete="email"
            inputMode="email"
            placeholder="nombre@dominio.com"
          />
        </div>

        {/* Edad y Género */}
        <div className="grid-2">
          <div className="field">
            <label htmlFor="edad">Edad</label>
            <input
              id="edad"
              name="edad"
              type="number"
              min="10"
              max="100"
              value={form.edad}
              onChange={handleChange}
              inputMode="numeric"
            />
          </div>
          <div className="field">
            <label htmlFor="genero">Género</label>
            <select
              id="genero"
              name="genero"
              value={form.genero}
              onChange={handleChange}
            >
              <option value="">Selecciona…</option>
              {GENEROS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Universidad */}
        <div className="field">
          <label htmlFor="universidad">Universidad</label>
          <select
            id="universidad"
            name="universidad"
            value={form.universidad}
            onChange={handleChange}
          >
            <option value="">Selecciona…</option>
            {universidades.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>

        {/* NivelEstudio + AñoEstudio */}
        <div className="grid-2">
          <div className="field">
            <label htmlFor="nivelEstudio">Nivel de Estudio</label>
            <select
              id="nivelEstudio"
              name="nivelEstudio"
              value={form.nivelEstudio}
              onChange={handleChange}
            >
              <option value="">Selecciona…</option>
              {NIVELES_ESTUDIO.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="anioEstudio">Año de Estudio</label>
            <select
              id="anioEstudio"
              name="anioEstudio"
              value={form.anioEstudio}
              onChange={handleChange}
              disabled={!form.nivelEstudio}
            >
              <option value="">
                {form.nivelEstudio ? 'Selecciona…' : 'Elegir Nivel primero'}
              </option>
              {aniosDisponibles.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Cuenta */}
        <div className="field">
          <label htmlFor="usuario">Nombre de usuario</label>
          <input
            id="usuario"
            name="usuario"
            value={form.usuario}
            onChange={handleChange}
            autoComplete="username"
          />
        </div>
        <div className="field">
          <label htmlFor="contrasena">Contraseña</label>
          <input
            id="contrasena"
            name="contrasena"
            type="password"
            value={form.contrasena}
            onChange={handleChange}
            autoComplete="new-password"
          />
        </div>

        <div className="acciones">
          <button className="btn btn-primary" onClick={registrarUsuario}>
            Crear Usuario
          </button>
          <button className="btn btn-secondary" onClick={handleVolver}>
            Regresar a Inicio
          </button>
        </div>
      </div>
    </div>
  );
};

export default PantallaRegistro;
